/**
 * Shared domain types — mirrors of the schema.sql enums (migration 0001), exactly.
 *
 * Convention: each enum is an `as const` tuple + a union type derived from it, so
 * zod schemas (`z.enum(LIFECYCLES)`), runtime validation and exhaustive switches
 * all share one source. If schema.sql gains a value, it is added HERE and nowhere
 * else. Do not re-declare these unions in verticals.
 */

export const LIFECYCLES = [
  "registered_unverified",
  "onboarding",
  "active",
  "paused",
  "deletion_grace",
  "banned",
  "purged",
] as const;
export type Lifecycle = (typeof LIFECYCLES)[number];
export type LifecycleState = Lifecycle;

export const GENDERS = ["woman", "man", "nonbinary"] as const;
export type Gender = (typeof GENDERS)[number];
/** show_me is a non-empty set of genders (schema.sql users.show_me). */
export type ShowMe = Gender[];

export const ROLES = ["user", "moderator", "admin"] as const;
export type Role = (typeof ROLES)[number];

export const VERDICTS = ["like", "crush", "reject", "super_reject"] as const;
export type Verdict = (typeof VERDICTS)[number];

export const PAIR_STATES = [
  "dormant",
  "half_open",
  "matched_classic",
  "matched_reject",
  "resolved_mixed",
  "resolved_suppressed",
  "closed",
] as const;
export type PairState = (typeof PAIR_STATES)[number];

export const CLOSED_REASONS = ["block", "unmatch", "account_close"] as const;
export type ClosedReason = (typeof CLOSED_REASONS)[number];

export const FUSE_STATES = ["fuse_running", "open", "expired"] as const;
export type FuseState = (typeof FUSE_STATES)[number];

export const MATCH_TYPES = ["classic", "reject", "roulette"] as const;
export type MatchType = (typeof MATCH_TYPES)[number];

export const COOLDOWN_SOURCES = ["sr_landed", "sr_reversed"] as const;
export type CooldownSource = (typeof COOLDOWN_SOURCES)[number];

export const SR_STATES = ["fired", "landed", "reversed", "rejected_invalid"] as const;
export type SrState = (typeof SR_STATES)[number];

export const SHIELD_STATES = ["unarmed", "armed"] as const;
export type ShieldState = (typeof SHIELD_STATES)[number];

export const ITEMS = [
  "shield",
  "berserker",
  "boost",
  "super_reject",
  "crush",
  "extend",
  "takeover_token",
] as const;
export type Item = (typeof ITEMS)[number];

export const LEDGER_REASONS = [
  "purchase",
  "consume",
  "refund_revoke",
  "rewind_refund",
  "sr_refund",
  "grant_killstreak",
  "grant_pity",
  "takeover_refund",
  "admin_adjust",
  "purge",
] as const;
export type LedgerReason = (typeof LEDGER_REASONS)[number];

export const CHECKOUT_STATES = ["pending", "credited", "expired", "refunded"] as const;
export type CheckoutState = (typeof CHECKOUT_STATES)[number];

export const SUB_STATUSES = ["active", "past_due", "cancel_scheduled", "lapsed"] as const;
export type SubStatus = (typeof SUB_STATUSES)[number];

export const SESSION_STATES = ["active", "expired"] as const;
export type SessionState = (typeof SESSION_STATES)[number];

export const TAKEOVER_STATES = [
  "offer_sent",
  "declined",
  "canceled",
  "offer_expired",
  "active",
  "expired",
  "killed_by_owner",
  "killed_by_mod",
  "closed_account_event",
] as const;
export type TakeoverState = (typeof TAKEOVER_STATES)[number];

export const CALL_KINDS = ["voice", "video"] as const;
export type CallKind = (typeof CALL_KINDS)[number];

export const CALL_STATES = ["ringing", "connecting", "active", "ended"] as const;
export type CallState = (typeof CALL_STATES)[number];

export const CALL_ENDS = [
  "declined",
  "canceled",
  "missed",
  "conn_fail",
  "hangup",
  "disconnect",
  "safety",
  "report",
] as const;
export type CallEnd = (typeof CALL_ENDS)[number];

export const ROULETTE_SESSION_STATES = ["matching", "connecting", "live", "ended"] as const;
export type RouletteSessionState = (typeof ROULETTE_SESSION_STATES)[number];

export const ROULETTE_END_REASONS = [
  "skip_a",
  "skip_b",
  "leave",
  "disconnect",
  "report",
  "block",
  "vibe_match",
  "mod_kill",
  "ban",
  "account_event",
  "conn_fail",
] as const;
export type RouletteEndReason = (typeof ROULETTE_END_REASONS)[number];

export const MEDIA_KINDS = [
  "photo",
  "profile_video",
  "voice_prompt",
  "chat_image",
  "chat_voice",
  "verification_selfie",
] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

export const MEDIA_STATUSES = ["uploaded", "scanning", "live", "quarantined", "removed"] as const;
export type MediaStatus = (typeof MEDIA_STATUSES)[number];

export const VERIFICATIONS = ["unverified", "pending", "verified", "rejected"] as const;
export type Verification = (typeof VERIFICATIONS)[number];

export const REPORT_STATUSES = ["open", "in_review", "resolved"] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export const REPORT_CATEGORIES = [
  "harassment",
  "hate",
  "impersonation",
  "underage_suspicion",
  "nsfw",
  "scam_spam",
  "violence_threat",
  "off_platform_harm",
  "other",
] as const;
export type ReportCategory = (typeof REPORT_CATEGORIES)[number];

export const REPORT_ACTIONS = [
  "no_action",
  "warn",
  "content_removed",
  "feature_restricted",
  "account_banned",
  "account_device_banned",
] as const;
export type ReportAction = (typeof REPORT_ACTIONS)[number];

export const RESTRICTION_SURFACES = [
  "roulette",
  "media_upload",
  "super_reject",
  "deck",
  "calls",
  "purchase",
] as const;
export type RestrictionSurface = (typeof RESTRICTION_SURFACES)[number];

export const MESSAGE_KINDS = ["text", "gif", "image", "voice", "system"] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];

export const COUNTER_KINDS = [
  "likes",
  "rejects",
  "super_rejects",
  "crushes",
  "rewinds",
  "opener_dms",
  "free_extends",
] as const;
export type CounterKind = (typeof COUNTER_KINDS)[number];

export const AUTH_TOKEN_PURPOSES = ["email_verify", "magic_link", "password_reset"] as const;
export type AuthTokenPurpose = (typeof AUTH_TOKEN_PURPOSES)[number];

export const EXPORT_STATES = [
  "queued",
  "building",
  "ready",
  "redeemed",
  "expired",
  "failed",
] as const;
export type ExportState = (typeof EXPORT_STATES)[number];

export const TIMER_KINDS = [
  "fuse_expire",
  "fuse_warn_6h",
  "fuse_warn_1h",
  "sr_cooldown_expire",
  "shield_window_expire",
  "berserker_expire",
  "boost_expire",
  "takeover_offer_ttl",
  "takeover_overlay_ttl",
  "rib_offer_ttl",
  "restriction_expire",
  "pair_recycle",
  "grace_expire",
  "unverified_purge",
  "onboarding_purge",
  "underage_lock_purge",
  "call_ring_timeout",
  "roulette_connect_timeout",
  "roulette_queue_timeout",
  "requeue_gate_release",
  "export_link_expire",
  "shield_log_archive",
  "roulette_meta_purge",
  "roulette_evidence_purge",
  "evidence_ttl",
  "checkout_retry",
  "premium_entitlement_end",
] as const;
export type TimerKind = (typeof TIMER_KINDS)[number];

// --- SM-14 "Play together" games (migration 0003 enum mirrors) --------------------------
export const GAME_KINDS = ["this_or_that", "two_truths", "tictactoe"] as const;
export type GameKind = (typeof GAME_KINDS)[number];

export const GAME_STATUSES = [
  "invited",
  "active",
  "completed",
  "declined",
  "expired",
  "abandoned",
] as const;
export type GameStatus = (typeof GAME_STATUSES)[number];

export const RELATIONSHIP_INTENTS = [
  "long_term",
  "short_term",
  "open_to_either",
  "friends_first",
  "here_for_the_plot",
  "unsure",
] as const;
export type RelationshipIntent = (typeof RELATIONSHIP_INTENTS)[number];

// --- Outbox / event plumbing (schema.sql outbox + event_log) ---------------------------
export const OUTBOX_CHANNELS = ["ws", "webpush", "email", "discord", "stripe"] as const;
export type OutboxChannel = (typeof OUTBOX_CHANNELS)[number];

export const ACTOR_ROLES = ["user", "system", "staff"] as const;
export type ActorRole = (typeof ACTOR_ROLES)[number];

/** Blocklist provenance (schema.sql blocks.via — render rule ARCH §3.1 correction 12). */
export const BLOCK_VIAS = ["profile", "sr_id", "roulette_session", "shield_log"] as const;
export type BlockVia = (typeof BLOCK_VIAS)[number];

/** SM-2 outcome aliases used in specs: Verdict/PairState pair. */
export type { Verdict as SwipeVerdict };

// --- Canonical pair key helpers -----------------------------------------------------------
/** Unordered pair, canonical (user_lo < user_hi by UUID string comparison — schema.sql). */
export interface PairKey {
  user_lo: string;
  user_hi: string;
}

/** Order two user ids into the canonical (lo, hi) pair key. */
export function pairKey(a: string, b: string): PairKey {
  if (a === b) throw new Error("pairKey: a pair needs two distinct users");
  return a < b ? { user_lo: a, user_hi: b } : { user_lo: b, user_hi: a };
}

/** Wire form of a pair key: `${lo}_${hi}` (openapi matchId). */
export function pairKeyString(a: string, b: string): string {
  const k = pairKey(a, b);
  return `${k.user_lo}_${k.user_hi}`;
}

export function parsePairKeyString(s: string): PairKey {
  const [lo, hi, extra] = s.split("_");
  if (!lo || !hi || extra !== undefined || !(lo < hi)) {
    throw new Error("invalid pair key string");
  }
  return { user_lo: lo, user_hi: hi };
}
